import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { subscribeToProducts, getInitialProducts, getProductById } from "../services/productService";
import Helmet from "../components/Helmet/Helmet";
import CommonSection from "../components/Ui/CommonSection";
import "../styles/product-details.css";
import ProductsList from "../components/Ui/ProductsList";
import { useDispatch, useSelector } from "react-redux";
import { cartActions } from "../redux/slices/cartSlice";
import { wishlistActions } from "../redux/slices/wishlistSlice";
import { toast } from "react-toastify";
import useAuth from "../custom-hooks/useAuth";
import { fetchProductReviews, addProductReview } from "../services/reviewService";
import RecentlyViewed from "../components/Ui/RecentlyViewed";
import { addToRecentlyViewed } from "../services/recentlyViewedService";
import { getProductImg, handleImgError } from "../utils/imageHelper";
import SizeGuideModal from "../components/Ui/SizeGuideModal";

const specDetails = {
  sofa: {
    dimensions: '88"W × 38"D × 34"H',
    materials: "Kiln-Dried FSC® Hardwood & Velvet",
    warranty: "10-Year Structural Frame Guarantee",
    delivery: "White-Glove In-Home Assembly",
  },
  chair: {
    dimensions: '32"W × 34"D × 36"H',
    materials: "Solid European Ash & Bouclé Fabric",
    warranty: "5-Year Craftsmanship Guarantee",
    delivery: "Express Courier (3-5 Business Days)",
  },
  table: {
    dimensions: '78"W × 38"D × 30"H (Standard Proportion)',
    materials: "Honed Natural Marble, Solid Ash & European Oak",
    warranty: "10-Year Structural Frame Guarantee",
    delivery: "White-Glove In-Home Assembly",
  },
  lighting: {
    dimensions: 'Adjustable Drop / Cord (Up to 72"H)',
    materials: "Solid Brushed Brass & Mouth-Blown Frosted Glass",
    warranty: "5-Year Fixture & Electrical Guarantee",
    delivery: "Insured Fragile-Care Express Delivery",
  },
  decor: {
    dimensions: 'Artisanal Dimensions (Handcrafted)',
    materials: "Stoneware Ceramic, Belgian Linen & Cast Brass",
    warranty: "3-Year Artisan Authenticity Guarantee",
    delivery: "Secure Shock-Proof Packaging",
  },
};

function ProductDetails() {
  const [tab, setTab] = useState("desc");
  const [quantity, setQuantity] = useState(1);
  const [isSizeGuideOpen, setIsSizeGuideOpen] = useState(false);
  
  // Authenticated review states
  const [userRating, setUserRating] = useState(5);
  const [reviewText, setReviewText] = useState("");
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const { id } = useParams();
  const [allProducts, setAllProducts] = useState(getInitialProducts);
  const [asyncProduct, setAsyncProduct] = useState(null);

  useEffect(() => {
    const unsubscribe = subscribeToProducts((latest) => {
      setAllProducts(latest);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    // If product not found in current cached products list, fetch it by ID directly
    const found = allProducts.find((item) => item.id === id);
    if (!found) {
      getProductById(id).then((p) => {
        if (p) setAsyncProduct(p);
      });
    }
  }, [id, allProducts]);

  const product = allProducts.find((item) => item.id === id) || asyncProduct;

  const [reviewsList, setReviewsList] = useState(product ? product.reviews || [] : []);

  const wishlistItems = useSelector((state) => state.wishlist.wishlistItems);
  const isWishlisted = product ? wishlistItems.some((w) => w.id === product.id) : false;

  useEffect(() => {
    window.scrollTo(0, 0);
    if (product) {
      setQuantity(1);
      setReviewText("");
      setUserRating(5);
      addToRecentlyViewed(product);

      // Fetch live verified reviews from Firestore
      const loadReviews = async () => {
        try {
          const remoteReviews = await fetchProductReviews(product.id);
          if (remoteReviews && remoteReviews.length > 0) {
            setReviewsList([...remoteReviews, ...(product.reviews || [])]);
          } else {
            setReviewsList(product.reviews || []);
          }
        } catch (err) {
          setReviewsList(product.reviews || []);
        }
      };
      loadReviews();
    }
  }, [product]);

  if (!product) {
    return (
      <Helmet title="Product Not Found | L'HABILLEUR">
        <CommonSection title="Product Not Found" />
        <Container className="py-5 text-center">
          <h2 className="fw-bold mb-3">Piece not found!</h2>
          <p className="text-muted mb-4">
            The piece you are looking for is no longer available in the current atelier catalog.
          </p>
          <Link to="/shop" className="buy__btn">
            Return to Collection
          </Link>
        </Container>
      </Helmet>
    );
  }

  const {
    productName,
    price,
    description,
    shortDesc,
    category,
  } = product;

  // Live calculated average rating and review count
  const totalRatingsSum = reviewsList.reduce((acc, curr) => acc + Number(curr.rating || 5), 0);
  const calculatedAvgRating = reviewsList.length > 0
    ? (totalRatingsSum / reviewsList.length).toFixed(1)
    : Number(product.avgRating || 4.8).toFixed(1);
  const totalReviewsCount = reviewsList.length;

  const specs = specDetails[category] || {
    dimensions: 'Standard Luxury Dimensions',
    materials: 'Artisanal Handcrafted Materials',
    warranty: '5-Year Atelier Warranty',
    delivery: 'Complimentary Express Courier',
  };

  const relatedProducts = allProducts.filter(
    (item) => item.category === category && item.id !== product.id
  ).slice(0, 4);

  const originalPrice = Math.round(price * 1.25);
  const installmentAmount = (price / 4).toFixed(2);

  const stockCount = product.stockQuantity !== undefined ? Number(product.stockQuantity) : product.stock !== undefined ? Number(product.stock) : 14;
  const isOutOfStock = product.inStock === false || stockCount <= 0;

  const handleAddToCart = () => {
    if (isOutOfStock) {
      toast.warning('This piece is currently fully committed / out of stock.');
      return;
    }

    if (!currentUser) {
      toast.info('Please sign in to add items to your cart & sync your profile');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    for (let i = 0; i < quantity; i++) {
      dispatch(
        cartActions.addItem({
          id: product.id,
          imgUrl: product.imgUrl,
          productName: product.productName,
          price: product.price,
        })
      );
    }
    toast.success(`Added ${quantity} piece${quantity > 1 ? 's' : ''} to shopping bag`);
  };

  const handleBuyNow = () => {
    if (!currentUser) {
      toast.info('Please sign in to proceed with your order');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    handleAddToCart();
    navigate('/checkout');
  };

  const handleToggleWishlist = () => {
    if (!currentUser) {
      toast.info('Please sign in to save pieces to your wishlist');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    dispatch(wishlistActions.toggleWishlist(product));
    if (isWishlisted) {
      toast.info('Removed from saved wishlist');
    } else {
      toast.success('Saved to your wishlist!');
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    if (!currentUser) {
      toast.info("Please sign in to submit a client appraisal");
      navigate('/login', { state: { from: location.pathname } });
      return;
    }

    if (!userRating || userRating < 1 || userRating > 5) {
      toast.error("Please choose a rating star (1 to 5)");
      return;
    }

    if (!reviewText.trim()) {
      toast.error("Please enter your appraisal commentary");
      return;
    }

    setIsSubmittingReview(true);
    try {
      const savedReview = await addProductReview({
        productId: product.id,
        userId: currentUser.uid,
        userName: currentUser.displayName,
        userEmail: currentUser.email,
        userPhoto: currentUser.photoURL,
        rating: userRating,
        text: reviewText.trim(),
      });

      setReviewsList((prev) => [savedReview, ...prev]);
      setReviewText("");
      setUserRating(5);
      toast.success("Thank you! Your verified appraisal has been recorded.");
    } catch (err) {
      toast.error("Failed to post appraisal. Please try again.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  return (
    <Helmet title={`${productName} | L'HABILLEUR`}>
      <CommonSection title={productName} />

      <section className="product__details-section">
        <Container>
          <Row className="g-5">
            {/* Left Column: Image Gallery Showcase */}
            <Col lg="6" md="12">
              <div className="product__gallery-wrapper">
                <div className="product__showcase-main">
                  <span className="product__showcase-badge">ATELIER CRAFT</span>
                  <img
                    src={getProductImg(product)}
                    onError={(e) => handleImgError(e, product)}
                    alt={productName}
                    width="500"
                    height="380"
                    fetchpriority="high"
                    decoding="async"
                  />
                </div>

                {/* Related Designs in this Category with instant navigation */}
                {relatedProducts.length > 0 && (
                  <div className="product__category-designs">
                    <div className="product__category-designs-header">
                      <span className="product__category-designs-title">
                        <i className="ri-layout-grid-line me-1"></i> More {category.toUpperCase()} Designs:
                      </span>
                      <span className="text-muted small">Select piece to switch</span>
                    </div>

                    <div className="product__thumbnails-row">
                      {/* Current Product */}
                      <div
                        className="product__thumb-item active current-piece"
                        title={`${productName} (Current Piece)`}
                      >
                        <img
                          src={getProductImg(product)}
                          onError={(e) => handleImgError(e, product)}
                          alt={productName}
                          width="70"
                          height="70"
                          loading="lazy"
                          decoding="async"
                        />
                        <span className="thumb-current-indicator">Current</span>
                      </div>

                      {/* Other Pieces in the Same Category */}
                      {relatedProducts.slice(0, 3).map((item) => (
                        <div
                          key={item.id}
                          className="product__thumb-item"
                          onClick={() => navigate(`/shop/${item.id}`)}
                          title={`Switch to: ${item.productName} ($${item.price})`}
                        >
                          <img
                            src={getProductImg(item)}
                            onError={(e) => handleImgError(e, item)}
                            alt={item.productName}
                            width="70"
                            height="70"
                            loading="lazy"
                            decoding="async"
                          />
                          <span className="thumb-price-tag">${item.price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Col>

            {/* Right Column: In-Depth Product Information & Actions */}
            <Col lg="6" md="12">
              <div className="product__info-wrapper">
                <div className="product__brand-label">L'HABILLEUR ATELIER &bull; {category.toUpperCase()}</div>
                <h1 className="product__title">{productName}</h1>

                {/* Stock Indicator & Rating */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                  <div className="product__stock-indicator">
                    <span
                      className="product__pulse-dot"
                      style={{
                        background: isOutOfStock
                          ? '#ef4444'
                          : stockCount <= 5
                          ? '#f59e0b'
                          : '#10b981',
                        boxShadow: isOutOfStock
                          ? '0 0 8px rgba(239, 68, 68, 0.7)'
                          : stockCount <= 5
                          ? '0 0 8px rgba(245, 158, 11, 0.7)'
                          : '0 0 8px rgba(16, 185, 129, 0.7)',
                      }}
                    ></span>
                    <span style={{ color: isOutOfStock ? '#f87171' : stockCount <= 5 ? '#fbbf24' : '#cbd5e1' }}>
                      {isOutOfStock
                        ? 'Out of Stock • Commission on Hold'
                        : stockCount <= 5
                        ? `Limited Stock • Only ${stockCount} pieces left in Atelier`
                        : `In Stock • ${stockCount} pieces ready for white-glove dispatch`}
                    </span>
                  </div>

                  <div className="product__rating-summary">
                    <div className="product__rating-stars">
                      <i className="ri-star-s-fill"></i>
                      <i className="ri-star-s-fill"></i>
                      <i className="ri-star-s-fill"></i>
                      <i className="ri-star-s-fill"></i>
                      <i className="ri-star-half-s-fill"></i>
                    </div>
                    <span className="product__rating-label">{calculatedAvgRating}</span>
                    <span
                      className="product__rating-link"
                      onClick={() => setTab("rev")}
                    >
                      ({totalReviewsCount} reviews)
                    </span>
                  </div>
                </div>

                {/* Pricing & Installments Box */}
                <div className="product__pricing-box">
                  <div className="product__price-row">
                    <span className="product__price-current">${price}</span>
                    <span className="product__price-original">${originalPrice}</span>
                    <span className="product__price-save">Save 20%</span>
                  </div>
                  <p className="product__installment-text">
                    <i className="ri-shield-flash-line me-1 text-primary"></i>
                    Or 4 interest-free installments of <strong>${installmentAmount}</strong> with Atelier Pay.
                  </p>
                </div>

                {/* Short Description */}
                <p className="product__description-snippet">{shortDesc}</p>

                {/* Interactive Purchase Controls */}
                <div className="product__cta-box">
                  <div className="product__cta-row">
                    <div className="product__qty-stepper">
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                        disabled={isOutOfStock}
                        aria-label="Decrease quantity"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span>{isOutOfStock ? 0 : quantity}</span>
                      <button
                        type="button"
                        onClick={() => setQuantity((q) => Math.min(stockCount, q + 1))}
                        disabled={isOutOfStock || quantity >= stockCount}
                        aria-label="Increase quantity"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>

                    <button
                      type="button"
                      className={`product__add-bag-btn ${isOutOfStock ? 'disabled opacity-50' : ''}`}
                      onClick={handleAddToCart}
                      disabled={isOutOfStock}
                    >
                      <i className="ri-shopping-bag-line"></i>{' '}
                      {isOutOfStock ? 'Fully Committed' : `Add to Bag ($${price * quantity})`}
                    </button>

                    <button
                      type="button"
                      className={`product__wishlist-cta ${isWishlisted ? 'active' : ''}`}
                      onClick={handleToggleWishlist}
                      title={isWishlisted ? 'Saved in Wishlist' : 'Save to Wishlist'}
                      aria-label="Toggle wishlist"
                    >
                      <i className={isWishlisted ? 'ri-heart-fill' : 'ri-heart-line'}></i>
                    </button>
                  </div>

                  <button
                    type="button"
                    className={`product__buy-now-btn ${isOutOfStock ? 'disabled opacity-50' : ''}`}
                    onClick={handleBuyNow}
                    disabled={isOutOfStock}
                  >
                    {isOutOfStock ? 'Piece Out of Stock' : (
                      <>
                        Buy Now with Instant Checkout <i className="ri-arrow-right-line ms-1"></i>
                      </>
                    )}
                  </button>
                </div>

                {/* Key Technical Specifications Grid */}
                <div className="product__specs-grid">
                  <div className="product__spec-card">
                    <div className="product__spec-icon">
                      <i className="ri-ruler-2-line"></i>
                    </div>
                    <div className="product__spec-content">
                      <span className="product__spec-label">Dimensions</span>
                      <span className="product__spec-value">{specs.dimensions}</span>
                    </div>
                  </div>

                  <div className="product__spec-card">
                    <div className="product__spec-icon">
                      <i className="ri-leaf-line"></i>
                    </div>
                    <div className="product__spec-content">
                      <span className="product__spec-label">Materials</span>
                      <span className="product__spec-value">{specs.materials}</span>
                    </div>
                  </div>

                  <div className="product__spec-card">
                    <div className="product__spec-icon">
                      <i className="ri-shield-star-line"></i>
                    </div>
                    <div className="product__spec-content">
                      <span className="product__spec-label">Warranty</span>
                      <span className="product__spec-value">{specs.warranty}</span>
                    </div>
                  </div>

                  <div className="product__spec-card">
                    <div className="product__spec-icon">
                      <i className="ri-truck-line"></i>
                    </div>
                    <div className="product__spec-content">
                      <span className="product__spec-label">Courier Delivery</span>
                      <span className="product__spec-value">{specs.delivery}</span>
                    </div>
                  </div>
                </div>

                {/* Size & Dimensions Guide Button */}
                <div className="mb-4">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill fw-semibold shadow-sm"
                    onClick={() => setIsSizeGuideOpen(true)}
                  >
                    <i className="ri-ruler-2-line text-warning fs-5"></i>
                    <span>View Architectural Dimensions &amp; Doorway Space Guide</span>
                  </button>
                </div>

                {/* 4 Trust & Assurance Value Cards */}
                <div className="product__assurance-row">
                  <div className="product__assurance-item">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span>Free White-Glove Courier Delivery</span>
                  </div>
                  <div className="product__assurance-item">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span>30-Day Risk-Free In-Home Trial</span>
                  </div>
                  <div className="product__assurance-item">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span>100% Certified Authentic Craft</span>
                  </div>
                  <div className="product__assurance-item">
                    <i className="ri-checkbox-circle-fill"></i>
                    <span>Cash on Delivery or Secure Card</span>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Tabs Section: Detailed Description & Customer Reviews */}
      <section className="pt-0">
        <Container>
          <Row>
            <Col lg="12">
              <div className="tab__wrapper">
                <button
                  type="button"
                  className={`tab__item ${tab === "desc" ? "active__tab" : ""}`}
                  onClick={() => setTab("desc")}
                >
                  Architectural Description
                </button>
                <button
                  type="button"
                  className={`tab__item ${tab === "rev" ? "active__tab" : ""}`}
                  onClick={() => setTab("rev")}
                >
                  Customer Reviews ({totalReviewsCount})
                </button>
              </div>

              {tab === "desc" ? (
                <div className="tab__content">
                  <p>{description}</p>
                </div>
              ) : (
                <div className="py-4">
                  <Row className="g-4">
                    {/* Reviews Breakdown & List */}
                    <Col lg="7" md="12">
                      <div className="reviews__score-card mb-4">
                        <h2>{calculatedAvgRating}</h2>
                        <div className="product__rating-stars justify-content-center mb-2">
                          <i className="ri-star-s-fill"></i>
                          <i className="ri-star-s-fill"></i>
                          <i className="ri-star-s-fill"></i>
                          <i className="ri-star-s-fill"></i>
                          <i className="ri-star-half-s-fill"></i>
                        </div>
                        <p className="text-muted small m-0">Based on verified atelier client appraisals</p>

                        <div className="mt-3 pt-3 border-top text-start">
                          <div className="reviews__breakdown-bar">
                            <span>5 Stars</span>
                            <div className="reviews__progress-track">
                              <div className="reviews__progress-fill" style={{ width: '85%' }}></div>
                            </div>
                            <span>85%</span>
                          </div>
                          <div className="reviews__breakdown-bar">
                            <span>4 Stars</span>
                            <div className="reviews__progress-track">
                              <div className="reviews__progress-fill" style={{ width: '12%' }}></div>
                            </div>
                            <span>12%</span>
                          </div>
                          <div className="reviews__breakdown-bar">
                            <span>3 Stars</span>
                            <div className="reviews__progress-track">
                              <div className="reviews__progress-fill" style={{ width: '3%' }}></div>
                            </div>
                            <span>3%</span>
                          </div>
                        </div>
                      </div>

                      <div className="reviews__list">
                        {reviewsList.length > 0 ? (
                          reviewsList.map((item, index) => (
                            <div key={item.id || index} className="review__item">
                              <div className="review__item-header">
                                <span className="review__author">
                                  {item.userName || "Verified Buyer"}
                                  <i className="ri-shield-check-fill" title="Verified Customer"></i>
                                </span>
                                <div className="review__stars">
                                  {[...Array(Math.round(item.rating || 5))].map((_, i) => (
                                    <i key={i} className="ri-star-s-fill"></i>
                                  ))}
                                </div>
                              </div>
                              <p className="review__text">{item.text}</p>
                            </div>
                          ))
                        ) : (
                          <div className="review__item">
                            <div className="review__item-header">
                              <span className="review__author">
                                Alexandra S.
                                <i className="ri-shield-check-fill"></i>
                              </span>
                              <div className="review__stars">
                                <i className="ri-star-s-fill"></i>
                                <i className="ri-star-s-fill"></i>
                                <i className="ri-star-s-fill"></i>
                                <i className="ri-star-s-fill"></i>
                                <i className="ri-star-s-fill"></i>
                              </div>
                            </div>
                            <p className="review__text">
                              Spectacular craftsmanship. The wood finish and upholstery texture are even more exquisite in person than on camera.
                            </p>
                          </div>
                        )}
                      </div>
                    </Col>

                    {/* Review Form - Exclusive to Authenticated Members */}
                    <Col lg="5" md="12">
                      {!currentUser ? (
                        <div className="review__form-card text-center py-4">
                          <div className="mb-3">
                            <i
                              className="ri-shield-user-line"
                              style={{ fontSize: "2.8rem", color: "#f8af3b" }}
                            ></i>
                          </div>
                          <h4 className="fw-bold mb-2">Member Appraisal Only</h4>
                          <p className="text-muted small mb-4" style={{ maxWidth: "300px", margin: "0 auto" }}>
                            To preserve authentic appraisals from verified atelier clients, reviews are exclusively available to registered members.
                          </p>
                          <button
                            type="button"
                            className="buy__btn w-100"
                            onClick={() =>
                              navigate("/login", { state: { from: location.pathname } })
                            }
                          >
                            Sign In to Leave an Appraisal &rarr;
                          </button>
                        </div>
                      ) : (
                        <div className="review__form-card">
                          <div className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom">
                            <div>
                              <h4 className="m-0">Share Your Appraisal</h4>
                              <span className="text-muted small">Verified Client Feedback</span>
                            </div>
                            <span className="badge bg-light text-dark border px-2 py-1 small">
                              <i className="ri-shield-check-fill text-warning me-1"></i>
                              {currentUser.displayName || currentUser.email?.split("@")[0]}
                            </span>
                          </div>

                          <form onSubmit={submitHandler}>
                            <div className="mb-3">
                              <label className="text-muted small fw-semibold d-block mb-1">
                                Your Rating
                              </label>
                              <div className="review__star-select">
                                {[1, 2, 3, 4, 5].map((num) => (
                                  <span
                                    key={num}
                                    onClick={() => setUserRating(num)}
                                    className={userRating >= num ? "selected" : ""}
                                    title={`${num} stars`}
                                  >
                                    <i className="ri-star-s-fill"></i>
                                  </span>
                                ))}
                              </div>
                            </div>

                            <div className="mb-3">
                              <label className="text-muted small fw-semibold d-block mb-1">
                                Your Experience & Craftsmanship Feedback
                              </label>
                              <textarea
                                rows={4}
                                placeholder="Describe the aesthetic, quality, ergonomics, and living comfort..."
                                className="review__textarea"
                                value={reviewText}
                                onChange={(e) => setReviewText(e.target.value)}
                                required
                              />
                            </div>

                            <button
                              type="submit"
                              className="buy__btn w-100"
                              disabled={isSubmittingReview}
                            >
                              {isSubmittingReview ? (
                                <>
                                  <i className="ri-loader-4-line ri-spin me-2"></i>
                                  Recording Appraisal...
                                </>
                              ) : (
                                "Submit Verified Appraisal"
                              )}
                            </button>
                          </form>
                        </div>
                      )}
                    </Col>
                  </Row>
                </div>
              )}
            </Col>

            {/* Curated Matching Pieces */}
            {relatedProducts.length > 0 && (
              <>
                <Col lg="12" className="mt-5 pt-3">
                  <h3 className="section__title">Curated Complementary Pieces</h3>
                </Col>
                <ProductsList data={relatedProducts} />
              </>
            )}

            {/* Recently Viewed Pieces */}
            <Col lg="12">
              <RecentlyViewed currentProductId={product.id} />
            </Col>
          </Row>
        </Container>
      </section>

      {/* Architectural Dimensions & Space Guide Modal */}
      <SizeGuideModal
        isOpen={isSizeGuideOpen}
        onClose={() => setIsSizeGuideOpen(false)}
        product={product}
      />
    </Helmet>
  );
}

export default ProductDetails;
